// client/src/coms/views/canvas_parts/canvas_room_view_parts/moveToolUtils.js
import { createOffscreenCanvas } from './canvasUtils';

const MIN_MOVE_TOOL_SIZE = 4;
export const MOVE_TOOL_HANDLE_SIZE = 12;
const DISTORT_GRID_SIZE = 8;
const PERSPECTIVE_MESH_SIZE = 24;

function clampNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function degreesToRadians(degrees) {
  return (clampNumber(degrees) * Math.PI) / 180;
}

function radiansToDegrees(radians) {
  return (clampNumber(radians) * 180) / Math.PI;
}

function normalizeDegrees(degrees) {
  let next = clampNumber(degrees) % 360;
  if (next < -180) next += 360;
  if (next > 180) next -= 360;
  return next;
}

function rotatePoint(x, y, radians) {
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  return { x: (x * cosine) - (y * sine), y: (x * sine) + (y * cosine) };
}

function defaultCorners(width, height) {
  const halfW = Math.max(MIN_MOVE_TOOL_SIZE, clampNumber(width, MIN_MOVE_TOOL_SIZE)) / 2;
  const halfH = Math.max(MIN_MOVE_TOOL_SIZE, clampNumber(height, MIN_MOVE_TOOL_SIZE)) / 2;
  return {
    nw: { x: -halfW, y: -halfH },
    ne: { x: halfW, y: -halfH },
    se: { x: halfW, y: halfH },
    sw: { x: -halfW, y: halfH },
  };
}

function cloneCorners(corners, width, height) {
  const fallback = defaultCorners(width, height);
  return Object.fromEntries(Object.keys(fallback).map((key) => [key, {
    x: clampNumber(corners?.[key]?.x, fallback[key].x),
    y: clampNumber(corners?.[key]?.y, fallback[key].y),
  }]));
}

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function localToWorld(state, point) {
  const rotated = rotatePoint(point.x, point.y, degreesToRadians(state.rotation));
  return { x: clampNumber(state.centerX) + rotated.x, y: clampNumber(state.centerY) + rotated.y };
}

function worldToLocal(drag, point) {
  return rotatePoint(point.x - drag.startCenterX, point.y - drag.startCenterY, -degreesToRadians(drag.startRotation));
}

export function getCanvasVisibleBounds(canvas) {
  if (!canvas || !canvas.width || !canvas.height) return null;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  let imageData;
  try { imageData = ctx.getImageData(0, 0, canvas.width, canvas.height); } catch { return null; }
  const data = imageData?.data;
  if (!data) return null;
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < canvas.height; y += 1) {
    const rowOffset = y * canvas.width * 4;
    for (let x = 0; x < canvas.width; x += 1) {
      if (data[rowOffset + (x * 4) + 3] <= 0) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < minX || maxY < minY) return null;
  return { x: minX, y: minY, width: Math.max(MIN_MOVE_TOOL_SIZE, maxX - minX + 1), height: Math.max(MIN_MOVE_TOOL_SIZE, maxY - minY + 1) };
}

export function createMoveToolState(layerId, layerCanvas) {
  const bounds = getCanvasVisibleBounds(layerCanvas);
  if (!bounds || !layerCanvas) return null;
  const sourceCanvas = createOffscreenCanvas(bounds.width, bounds.height);
  const sourceCtx = sourceCanvas.getContext('2d', { willReadFrequently: true });
  if (!sourceCtx) return null;
  sourceCtx.drawImage(layerCanvas, bounds.x, bounds.y, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
  return {
    layerId,
    sourceCanvas,
    sourceBounds: { x: 0, y: 0, width: bounds.width, height: bounds.height },
    centerX: bounds.x + (bounds.width / 2),
    centerY: bounds.y + (bounds.height / 2),
    width: bounds.width,
    height: bounds.height,
    rotation: 0,
    flipX: false,
    flipY: false,
    corners: defaultCorners(bounds.width, bounds.height),
    distortGrid: null,
    cropBounds: { left: bounds.x, top: bounds.y, right: bounds.x + bounds.width, bottom: bounds.y + bounds.height },
  };
}


function createDistortGrid(state, size = DISTORT_GRID_SIZE) {
  const count = Math.max(2, Math.round(size));
  const sourceWidth = Math.max(1, state.sourceBounds?.width || state.width || 1);
  const sourceHeight = Math.max(1, state.sourceBounds?.height || state.height || 1);
  const corners = cloneCorners(state.corners, state.width, state.height);
  const worldCorners = Object.fromEntries(Object.entries(corners).map(([key, point]) => [key, localToWorld(state, point)]));
  const sourcePoints = [];
  const points = [];
  for (let row = 0; row < count; row += 1) {
    const v = row / (count - 1);
    for (let col = 0; col < count; col += 1) {
      const u = col / (count - 1);
      sourcePoints.push({ x: u * sourceWidth, y: v * sourceHeight });
      const topX = worldCorners.nw.x + ((worldCorners.ne.x - worldCorners.nw.x) * u);
      const topY = worldCorners.nw.y + ((worldCorners.ne.y - worldCorners.nw.y) * u);
      const bottomX = worldCorners.sw.x + ((worldCorners.se.x - worldCorners.sw.x) * u);
      const bottomY = worldCorners.sw.y + ((worldCorners.se.y - worldCorners.sw.y) * u);
      points.push({ x: topX + ((bottomX - topX) * v), y: topY + ((bottomY - topY) * v) });
    }
  }
  return { size: count, sourcePoints, points };
}

export function ensureMoveToolDistortGrid(state) {
  if (!state) return state;
  if (state.distortGrid?.points?.length) return state;
  return { ...state, distortGrid: createDistortGrid(state) };
}

export function getMoveToolDistortGridPoints(state) {
  return Array.isArray(state?.distortGrid?.points) ? state.distortGrid.points.map((point) => ({ ...point })) : [];
}

export function getMoveToolDistortGridSegments(state) {
  const grid = state?.distortGrid;
  if (!grid?.points?.length) return [];
  const size = Math.max(2, Math.round(grid.size || DISTORT_GRID_SIZE));
  const segments = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      segments.push({ start: grid.points[(row * size) + col], end: grid.points[(row * size) + col + 1] });
    }
  }
  for (let col = 0; col < size; col += 1) {
    for (let row = 0; row < size - 1; row += 1) {
      segments.push({ start: grid.points[(row * size) + col], end: grid.points[((row + 1) * size) + col] });
    }
  }
  return segments;
}

function drawImageTriangle(ctx, image, source, destination) {
  const [s0, s1, s2] = source;
  const [d0, d1, d2] = destination;
  const denom = (s0.x * (s1.y - s2.y)) + (s1.x * (s2.y - s0.y)) + (s2.x * (s0.y - s1.y));
  if (Math.abs(denom) < 0.000001) return;
  const a = ((d0.x * (s1.y - s2.y)) + (d1.x * (s2.y - s0.y)) + (d2.x * (s0.y - s1.y))) / denom;
  const c = ((d0.x * (s2.x - s1.x)) + (d1.x * (s0.x - s2.x)) + (d2.x * (s1.x - s0.x))) / denom;
  const e = ((d0.x * ((s1.x * s2.y) - (s2.x * s1.y))) + (d1.x * ((s2.x * s0.y) - (s0.x * s2.y))) + (d2.x * ((s0.x * s1.y) - (s1.x * s0.y)))) / denom;
  const b = ((d0.y * (s1.y - s2.y)) + (d1.y * (s2.y - s0.y)) + (d2.y * (s0.y - s1.y))) / denom;
  const d = ((d0.y * (s2.x - s1.x)) + (d1.y * (s0.x - s2.x)) + (d2.y * (s1.x - s0.x))) / denom;
  const f = ((d0.y * ((s1.x * s2.y) - (s2.x * s1.y))) + (d1.y * ((s2.x * s0.y) - (s0.x * s2.y))) + (d2.y * ((s0.x * s1.y) - (s1.x * s0.y)))) / denom;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(d0.x, d0.y);
  ctx.lineTo(d1.x, d1.y);
  ctx.lineTo(d2.x, d2.y);
  ctx.closePath();
  ctx.clip();
  ctx.setTransform(a, b, c, d, e, f);
  ctx.drawImage(image, 0, 0);
  ctx.restore();
}


function getProjectivePoint(corners, u, v) {
  const p0 = corners.nw;
  const p1 = corners.ne;
  const p2 = corners.se;
  const p3 = corners.sw;
  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const dy3 = p0.y - p1.y + p2.y - p3.y;

  let a;
  let b;
  let c;
  let d;
  let e;
  let f;
  let g = 0;
  let h = 0;

  if (Math.abs(dx3) < 0.000001 && Math.abs(dy3) < 0.000001) {
    a = p1.x - p0.x;
    b = p3.x - p0.x;
    c = p0.x;
    d = p1.y - p0.y;
    e = p3.y - p0.y;
    f = p0.y;
  } else {
    const denominator = (dx1 * dy2) - (dx2 * dy1);
    if (Math.abs(denominator) < 0.000001) {
      const topX = p0.x + ((p1.x - p0.x) * u);
      const topY = p0.y + ((p1.y - p0.y) * u);
      const bottomX = p3.x + ((p2.x - p3.x) * u);
      const bottomY = p3.y + ((p2.y - p3.y) * u);
      return {
        x: topX + ((bottomX - topX) * v),
        y: topY + ((bottomY - topY) * v),
      };
    }
    g = ((dx3 * dy2) - (dx2 * dy3)) / denominator;
    h = ((dx1 * dy3) - (dx3 * dy1)) / denominator;
    a = p1.x - p0.x + (g * p1.x);
    b = p3.x - p0.x + (h * p3.x);
    c = p0.x;
    d = p1.y - p0.y + (g * p1.y);
    e = p3.y - p0.y + (h * p3.y);
    f = p0.y;
  }

  const scale = (g * u) + (h * v) + 1;
  if (Math.abs(scale) < 0.000001) return { x: p0.x, y: p0.y };
  return {
    x: ((a * u) + (b * v) + c) / scale,
    y: ((d * u) + (e * v) + f) / scale,
  };
}

function drawPerspectiveMesh(ctx, state, worldCorners) {
  const sourceWidth = Math.max(1, state.sourceBounds.width);
  const sourceHeight = Math.max(1, state.sourceBounds.height);
  const size = PERSPECTIVE_MESH_SIZE;
  for (let row = 0; row < size; row += 1) {
    const v0 = row / size;
    const v1 = (row + 1) / size;
    for (let col = 0; col < size; col += 1) {
      const u0 = col / size;
      const u1 = (col + 1) / size;
      const sourceA = { x: u0 * sourceWidth, y: v0 * sourceHeight };
      const sourceB = { x: u1 * sourceWidth, y: v0 * sourceHeight };
      const sourceC = { x: u0 * sourceWidth, y: v1 * sourceHeight };
      const sourceD = { x: u1 * sourceWidth, y: v1 * sourceHeight };
      const destA = getProjectivePoint(worldCorners, u0, v0);
      const destB = getProjectivePoint(worldCorners, u1, v0);
      const destC = getProjectivePoint(worldCorners, u0, v1);
      const destD = getProjectivePoint(worldCorners, u1, v1);
      drawImageTriangle(ctx, state.sourceCanvas, [sourceA, sourceB, sourceD], [destA, destB, destD]);
      drawImageTriangle(ctx, state.sourceCanvas, [sourceA, sourceD, sourceC], [destA, destD, destC]);
    }
  }
}

export function drawMoveToolStateToLayer(layerCanvas, state) {
  if (!layerCanvas || !state?.sourceCanvas || !state?.sourceBounds) return false;
  const ctx = layerCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.clearRect(0, 0, layerCanvas.width, layerCanvas.height);

  if (state.distortGrid?.points?.length && state.distortGrid?.sourcePoints?.length) {
    const grid = state.distortGrid;
    const size = Math.max(2, Math.round(grid.size || DISTORT_GRID_SIZE));
    for (let row = 0; row < size - 1; row += 1) {
      for (let col = 0; col < size - 1; col += 1) {
        const a = (row * size) + col;
        const b = a + 1;
        const c = a + size;
        const d = c + 1;
        drawImageTriangle(ctx, state.sourceCanvas, [grid.sourcePoints[a], grid.sourcePoints[b], grid.sourcePoints[d]], [grid.points[a], grid.points[b], grid.points[d]]);
        drawImageTriangle(ctx, state.sourceCanvas, [grid.sourcePoints[a], grid.sourcePoints[d], grid.sourcePoints[c]], [grid.points[a], grid.points[d], grid.points[c]]);
      }
    }
    return true;
  }

  const sourceWidth = state.sourceBounds.width;
  const sourceHeight = state.sourceBounds.height;
  const corners = cloneCorners(state.corners, state.width, state.height);
  const world = Object.fromEntries(Object.entries(corners).map(([key, point]) => [key, localToWorld(state, point)]));

  if (state.transformMode === 'perspective') {
    drawPerspectiveMesh(ctx, state, world);
    return true;
  }

  const sx0 = state.flipX ? sourceWidth : 0;
  const sx1 = state.flipX ? 0 : sourceWidth;
  const sy0 = state.flipY ? sourceHeight : 0;
  const sy1 = state.flipY ? 0 : sourceHeight;
  const source = {
    nw: { x: sx0, y: sy0 }, ne: { x: sx1, y: sy0 },
    se: { x: sx1, y: sy1 }, sw: { x: sx0, y: sy1 },
  };

  drawImageTriangle(ctx, state.sourceCanvas, [source.nw, source.ne, source.se], [world.nw, world.ne, world.se]);
  drawImageTriangle(ctx, state.sourceCanvas, [source.nw, source.se, source.sw], [world.nw, world.se, world.sw]);
  return true;
}

export function createMoveToolDragState(mode, handle, point, state, transformMode = 'scale', perspectiveAxis = 'vertical') {
  if (!point || !state) return null;
  const centerX = clampNumber(state.centerX);
  const centerY = clampNumber(state.centerY);
  return {
    mode,
    transformMode,
    perspectiveAxis: perspectiveAxis === 'horizontal' ? 'horizontal' : 'vertical',
    handle: String(handle || ''),
    startPoint: { x: point.x, y: point.y },
    startCenterX: centerX,
    startCenterY: centerY,
    startWidth: Math.max(MIN_MOVE_TOOL_SIZE, clampNumber(state.width, MIN_MOVE_TOOL_SIZE)),
    startHeight: Math.max(MIN_MOVE_TOOL_SIZE, clampNumber(state.height, MIN_MOVE_TOOL_SIZE)),
    startRotation: clampNumber(state.rotation),
    startCorners: cloneCorners(state.corners, state.width, state.height),
    startDistortGrid: state.distortGrid ? { ...state.distortGrid, sourcePoints: state.distortGrid.sourcePoints.map((p) => ({ ...p })), points: state.distortGrid.points.map((p) => ({ ...p })) } : null,
    startPointerAngle: Math.atan2(point.y - centerY, point.x - centerX),
    startCropBounds: state.cropBounds ? { ...state.cropBounds } : null,
  };
}

function updateScale(state, drag, localPointer) {
  const corners = cloneCorners(drag.startCorners, drag.startWidth, drag.startHeight);
  const handle = drag.handle;
  const opposite = { nw: 'se', n: 's', ne: 'sw', e: 'w', se: 'nw', s: 'n', sw: 'ne', w: 'e' }[handle];
  const handles = {
    nw: corners.nw, n: midpoint(corners.nw, corners.ne), ne: corners.ne,
    e: midpoint(corners.ne, corners.se), se: corners.se, s: midpoint(corners.sw, corners.se),
    sw: corners.sw, w: midpoint(corners.nw, corners.sw),
  };
  const fixed = handles[opposite];
  if (!fixed) return state;
  let left = Math.min(fixed.x, localPointer.x);
  let right = Math.max(fixed.x, localPointer.x);
  let top = Math.min(fixed.y, localPointer.y);
  let bottom = Math.max(fixed.y, localPointer.y);
  if (handle === 'n' || handle === 's') { left = Math.min(corners.nw.x, corners.sw.x); right = Math.max(corners.ne.x, corners.se.x); }
  if (handle === 'e' || handle === 'w') { top = Math.min(corners.nw.y, corners.ne.y); bottom = Math.max(corners.sw.y, corners.se.y); }
  if (right - left < MIN_MOVE_TOOL_SIZE || bottom - top < MIN_MOVE_TOOL_SIZE) return state;
  const localCenter = { x: (left + right) / 2, y: (top + bottom) / 2 };
  const worldOffset = rotatePoint(localCenter.x, localCenter.y, degreesToRadians(drag.startRotation));
  return {
    ...state,
    centerX: drag.startCenterX + worldOffset.x,
    centerY: drag.startCenterY + worldOffset.y,
    width: right - left,
    height: bottom - top,
    corners: defaultCorners(right - left, bottom - top),
    transformMode: 'scale',
  };
}

function updateCornerTransform(state, drag, localPointer) {
  const corners = cloneCorners(drag.startCorners, drag.startWidth, drag.startHeight);
  const handle = drag.handle;
  const mode = drag.transformMode;
  const handles = {
    nw: corners.nw,
    n: midpoint(corners.nw, corners.ne),
    ne: corners.ne,
    e: midpoint(corners.ne, corners.se),
    se: corners.se,
    s: midpoint(corners.sw, corners.se),
    sw: corners.sw,
    w: midpoint(corners.nw, corners.sw),
  };
  const startHandle = handles[handle] || { x: 0, y: 0 };
  const delta = { x: localPointer.x - startHandle.x, y: localPointer.y - startHandle.y };

  if (mode === 'distort' && corners[handle]) {
    corners[handle] = localPointer;
  } else if (mode === 'skew') {
    if (handle === 'n') { corners.nw.x += delta.x; corners.ne.x += delta.x; }
    else if (handle === 's') { corners.sw.x += delta.x; corners.se.x += delta.x; }
    else if (handle === 'w') { corners.nw.y += delta.y; corners.sw.y += delta.y; }
    else if (handle === 'e') { corners.ne.y += delta.y; corners.se.y += delta.y; }
    else if (corners[handle]) corners[handle] = localPointer;
  } else if (mode === 'perspective') {
    if (!corners[handle]) return state;
    const axis = drag.perspectiveAxis === 'horizontal' ? 'horizontal' : 'vertical';
    const paired = axis === 'vertical'
      ? { nw: 'ne', ne: 'nw', sw: 'se', se: 'sw' }[handle]
      : { nw: 'sw', sw: 'nw', ne: 'se', se: 'ne' }[handle];
    if (!paired || !corners[paired]) return state;

    if (axis === 'vertical') {
      corners[handle] = {
        x: localPointer.x,
        y: localPointer.y,
      };
      corners[paired] = {
        x: corners[paired].x - delta.x,
        y: corners[paired].y + delta.y,
      };
    } else {
      corners[handle] = {
        x: localPointer.x,
        y: localPointer.y,
      };
      corners[paired] = {
        x: corners[paired].x + delta.x,
        y: corners[paired].y - delta.y,
      };
    }
  } else {
    return updateScale(state, drag, localPointer);
  }

  return { ...state, corners, transformMode: mode };
}

function updateCropBounds(state, drag, point) {
  const start = drag.startCropBounds || state.cropBounds || {
    left: 0,
    top: 0,
    right: Math.max(MIN_MOVE_TOOL_SIZE, state.documentWidth || state.width),
    bottom: Math.max(MIN_MOVE_TOOL_SIZE, state.documentHeight || state.height),
  };
  const next = { ...start };
  const handle = drag.handle;
  if (handle.includes('w')) next.left = Math.min(point.x, next.right - MIN_MOVE_TOOL_SIZE);
  if (handle.includes('e')) next.right = Math.max(point.x, next.left + MIN_MOVE_TOOL_SIZE);
  if (handle.includes('n')) next.top = Math.min(point.y, next.bottom - MIN_MOVE_TOOL_SIZE);
  if (handle.includes('s')) next.bottom = Math.max(point.y, next.top + MIN_MOVE_TOOL_SIZE);
  if (handle === 'n') next.top = Math.min(point.y, next.bottom - MIN_MOVE_TOOL_SIZE);
  if (handle === 's') next.bottom = Math.max(point.y, next.top + MIN_MOVE_TOOL_SIZE);
  if (handle === 'w') next.left = Math.min(point.x, next.right - MIN_MOVE_TOOL_SIZE);
  if (handle === 'e') next.right = Math.max(point.x, next.left + MIN_MOVE_TOOL_SIZE);
  const maxW = Math.max(MIN_MOVE_TOOL_SIZE, Number(state.documentWidth) || next.right);
  const maxH = Math.max(MIN_MOVE_TOOL_SIZE, Number(state.documentHeight) || next.bottom);
  next.left = Math.max(0, Math.min(maxW - MIN_MOVE_TOOL_SIZE, next.left));
  next.top = Math.max(0, Math.min(maxH - MIN_MOVE_TOOL_SIZE, next.top));
  next.right = Math.max(next.left + MIN_MOVE_TOOL_SIZE, Math.min(maxW, next.right));
  next.bottom = Math.max(next.top + MIN_MOVE_TOOL_SIZE, Math.min(maxH, next.bottom));
  return { ...state, cropBounds: next };
}

export function applyLayerCrop(state) {
  if (!state?.sourceCanvas) return state;
  const bounds = state.cropBounds;
  if (!bounds) return state;
  const left = Math.max(0, Math.floor(bounds.left));
  const top = Math.max(0, Math.floor(bounds.top));
  const right = Math.min(Math.max(left + 1, Math.ceil(bounds.right)), Math.max(1, Number(state.documentWidth) || Math.ceil(bounds.right)));
  const bottom = Math.min(Math.max(top + 1, Math.ceil(bounds.bottom)), Math.max(1, Number(state.documentHeight) || Math.ceil(bounds.bottom)));
  const width = Math.max(1, right - left);
  const height = Math.max(1, bottom - top);
  const output = createOffscreenCanvas(width, height);
  const ctx = output.getContext('2d', { willReadFrequently: true });
  if (!ctx) return state;
  const originalLeft = state.centerX - (state.width / 2);
  const originalTop = state.centerY - (state.height / 2);
  ctx.drawImage(state.sourceCanvas, originalLeft - left, originalTop - top);
  return {
    ...state,
    sourceCanvas: output,
    sourceBounds: { x: 0, y: 0, width, height },
    centerX: left + (width / 2),
    centerY: top + (height / 2),
    width,
    height,
    rotation: 0,
    flipX: false,
    flipY: false,
    corners: defaultCorners(width, height),
    cropBounds: { left, top, right, bottom },
  };
}

export function updateMoveToolStateFromDrag(state, drag, point) {
  if (!state || !drag || !point) return state || null;
  if (drag.mode === 'distortPoint') {
    const grid = drag.startDistortGrid || createDistortGrid(state);
    const index = Math.max(0, Math.min(grid.points.length - 1, Number(drag.handle) || 0));
    const points = grid.points.map((gridPoint) => ({ ...gridPoint }));
    points[index] = { x: point.x, y: point.y };
    return { ...state, distortGrid: { ...grid, points } };
  }
  if (drag.mode === 'move') {
    if (drag.transformMode === 'cropLayer' && drag.startCropBounds) {
      const dx = point.x - drag.startPoint.x;
      const dy = point.y - drag.startPoint.y;
      const width = drag.startCropBounds.right - drag.startCropBounds.left;
      const height = drag.startCropBounds.bottom - drag.startCropBounds.top;
      const maxW = Math.max(width, Number(state.documentWidth) || drag.startCropBounds.right);
      const maxH = Math.max(height, Number(state.documentHeight) || drag.startCropBounds.bottom);
      const left = Math.max(0, Math.min(maxW - width, drag.startCropBounds.left + dx));
      const top = Math.max(0, Math.min(maxH - height, drag.startCropBounds.top + dy));
      return { ...state, cropBounds: { left, top, right: left + width, bottom: top + height } };
    }
    return { ...state, centerX: drag.startCenterX + point.x - drag.startPoint.x, centerY: drag.startCenterY + point.y - drag.startPoint.y };
  }
  if (drag.mode === 'rotate') {
    const currentAngle = Math.atan2(point.y - drag.startCenterY, point.x - drag.startCenterX);
    return { ...state, rotation: normalizeDegrees(drag.startRotation + radiansToDegrees(currentAngle - drag.startPointerAngle)) };
  }
  if (drag.mode !== 'resize') return state;
  if (drag.transformMode === 'cropLayer') return updateCropBounds(state, drag, point);
  return updateCornerTransform(state, drag, worldToLocal(drag, point));
}

export function applyMoveToolCommand(state, command) {
  if (!state) return state;
  if (command === 'rotate90cw') return { ...state, rotation: normalizeDegrees(state.rotation + 90) };
  if (command === 'rotate90ccw') return { ...state, rotation: normalizeDegrees(state.rotation - 90) };
  if (command === 'rotate180') return { ...state, rotation: normalizeDegrees(state.rotation + 180) };
  if (command === 'flipHorizontal') return { ...state, flipX: !state.flipX };
  if (command === 'flipVertical') return { ...state, flipY: !state.flipY };
  return state;
}

export function getMoveToolWorldHandles(state, transformMode = 'scale') {
  if (!state) return {};
  if (transformMode === 'cropLayer' && state.cropBounds) {
    const { left, top, right, bottom } = state.cropBounds;
    return {
      nw: { x: left, y: top }, n: { x: (left + right) / 2, y: top }, ne: { x: right, y: top },
      e: { x: right, y: (top + bottom) / 2 }, se: { x: right, y: bottom }, s: { x: (left + right) / 2, y: bottom },
      sw: { x: left, y: bottom }, w: { x: left, y: (top + bottom) / 2 },
    };
  }
  const corners = cloneCorners(state.corners, state.width, state.height);
  const local = {
    nw: corners.nw, n: midpoint(corners.nw, corners.ne), ne: corners.ne,
    e: midpoint(corners.ne, corners.se), se: corners.se, s: midpoint(corners.sw, corners.se),
    sw: corners.sw, w: midpoint(corners.nw, corners.sw),
  };
  return Object.fromEntries(Object.entries(local).map(([key, point]) => [key, localToWorld(state, point)]));
}

export function getMoveToolOverlayBoxStyle() {
  return { position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 35 };
}

export function getMoveToolHandleStyles() {
  return {};
}
